import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Breadcrumbs,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert,
  useTheme,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tooltip
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import Layout from '../../components/layout/Layout';
import { useSnackbar } from 'notistack';
import * as XLSX from 'xlsx';
import axios from 'axios';
import API_CONFIG from '../../config/api.config';
import { getAuthHeader } from '../../services/authService';
import { formatCurrency } from '../../utils/currencyFormatter';
import physicalStockService from '../../services/physicalStockService';
import productService from '../../services/productService';
import salesService from '../../services/salesService';

const InvoiceProfitLossReport = () => {
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();

  // Get current date and first day of current month
  const getCurrentDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0]; // Format: YYYY-MM-DD
  };

  const getCurrentMonthStart = () => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    return firstDay.toISOString().split('T')[0]; // Format: YYYY-MM-DD
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit'
    });
  };

  // State management
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isExporting, setIsExporting] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedOrderItems, setSelectedOrderItems] = useState([]);

  // Search and filter states - Initialize with current date only (not whole month)
  const [startDate, setStartDate] = useState(getCurrentDate());
  const [endDate, setEndDate] = useState(getCurrentDate());
  const [searchQuery, setSearchQuery] = useState('');

  // Data state
  const [data, setData] = useState({
    totalInvoices: 0,
    totalRevenue: 0,
    totalProfit: 0,
    totalLoss: 0,
    invoiceDetails: [],
    physicalAdjustmentAmount: 0,
    physicalPositiveAmount: 0,
    physicalNegativeAmount: 0
  });

  // Handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.INVOICE_PROFIT_LOSS_REPORT}`, {
        headers: getAuthHeader(),
        params: {
          startDate,
          endDate,
          searchQuery
        }
      });

      const baseData = response.data || {};

      const stockRes = await physicalStockService.getAll();
      const stockList = Array.isArray(stockRes) ? stockRes : (stockRes?.content || []);

      const s = new Date(startDate);
      const e = new Date(endDate);
      e.setHours(23, 59, 59, 999);

      const toDate = (value) => {
        if (!value) return null;
        if (Array.isArray(value)) {
          const [y, m, d, hh = 0, mm = 0, ss = 0] = value;
          return new Date(y, m - 1, d, hh, mm, ss);
        }
        const dt = new Date(value);
        return isNaN(dt.getTime()) ? null : dt;
      };

      const filteredStocks = (stockList || []).filter(ps => {
        const ts = ps.stockCheckDate ? toDate(ps.stockCheckDate) : null;
        const withinDate = ts ? ts >= s && ts <= e : true;
        return withinDate;
      });

      const details = filteredStocks.flatMap(m => (m.details || []));
      const DAMAGE_REASON = 'Damage/Breakage';
      const productIds = Array.from(new Set(details.map(d => d.productId).filter(Boolean)));

      const products = await Promise.all(productIds.map(async (id) => {
        try {
          const p = await productService.getProductById(id);
          return { id, p };
        } catch {
          return { id, p: null };
        }
      }));
      const priceMap = new Map(products.map(({ id, p }) => [id, (p?.purchasePrice ?? p?.costPrice ?? 0) || 0]));

      let physicalPositiveAmount = 0;
      let physicalNegativeAmount = 0;
      details.forEach(d => {
        if ((d?.reason || '') === DAMAGE_REASON) {
          const diff = Number(d?.difference || 0);
          const price = priceMap.get(d.productId) || 0;
          const amt = diff * price;
          if (amt > 0) physicalPositiveAmount += amt;
          if (amt < 0) physicalNegativeAmount += amt;
        }
      });
      const physicalAdjustmentAmount = Number((physicalPositiveAmount + physicalNegativeAmount).toFixed(2));

      const baseProfit = Number(baseData.totalProfit || 0);
      const baseLoss = Number(baseData.totalLoss || 0);
      const netBefore = baseProfit - baseLoss;
      const netAfter = netBefore + physicalAdjustmentAmount;
      const adjustedProfit = netAfter > 0 ? Number(netAfter.toFixed(2)) : 0;
      const adjustedLoss = netAfter < 0 ? Number(Math.abs(netAfter).toFixed(2)) : 0;

      // Build physical adjustment rows for table
      const physicalDamageRows = [];
      const physicalOtherRows = [];
      filteredStocks.forEach(m => {
        const details = m.details || [];
        const damageSum = details.reduce((sum, d) => {
          if ((d?.reason || '') !== DAMAGE_REASON) return sum;
          const price = priceMap.get(d.productId) || 0;
          return sum + (Number(d?.difference || 0) * price);
        }, 0);
        const otherReasonsSet = new Set();
        const otherSum = details.reduce((sum, d) => {
          const r = (d?.reason || '');
          if (!r || r === DAMAGE_REASON) return sum;
          otherReasonsSet.add(r);
          const price = priceMap.get(d.productId) || 0;
          return sum + (Number(d?.difference || 0) * price);
        }, 0);

        if (damageSum !== 0) {
          physicalDamageRows.push({
            invoiceNo: m.referenceNumber || `PS-${m.id}`,
            date: toDate(m.stockCheckDate),
            customerName: '',
            revenue: 0,
            cost: 0,
            profit: Number(damageSum.toFixed(2)),
            margin: null,
            type: 'physical',
            reason: DAMAGE_REASON
          });
        }
        if (otherSum !== 0) {
          physicalOtherRows.push({
            invoiceNo: m.referenceNumber || `PS-${m.id}`,
            date: toDate(m.stockCheckDate),
            customerName: '',
            revenue: 0,
            cost: 0,
            profit: Number(otherSum.toFixed(2)),
            margin: null,
            type: 'physical',
            reason: Array.from(otherReasonsSet).join(', ')
          });
        }
      });

      const q = (searchQuery || '').trim().toLowerCase();
      const baseInvoiceRows = (baseData.invoiceDetails || []);
      const filteredInvoiceRows = q
        ? baseInvoiceRows.filter(inv => {
          const invNo = (inv.invoiceNo || '').toLowerCase();
          const cust = (inv.customerName || '').toLowerCase();
          return invNo.includes(q) || cust.includes(q);
        })
        : baseInvoiceRows;

      const combinedRows = q
        ? filteredInvoiceRows
        : [...filteredInvoiceRows, ...physicalDamageRows, ...physicalOtherRows];

      setData({
        ...baseData,
        totalProfit: adjustedProfit,
        totalLoss: adjustedLoss,
        physicalAdjustmentAmount,
        physicalPositiveAmount: Number(Math.max(0, physicalPositiveAmount).toFixed(2)),
        physicalNegativeAmount: Number(Math.abs(Math.min(0, physicalNegativeAmount)).toFixed(2)),
        invoiceDetails: combinedRows
      });
    } catch (err) {
      setError('Failed to fetch report data. Please try again.');
      enqueueSnackbar('Error generating report', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleExportToExcel = async () => {
    if (data.invoiceDetails.length === 0) {
      enqueueSnackbar('No data to export', { variant: 'warning' });
      return;
    }

    setIsExporting(true);
    try {
      // Include invoice rows and physical adjustment rows (marked via "Type")
      const exportData = data.invoiceDetails.map(invoice => {
        const isPhysical = invoice.type === 'physical';
        return {
          'Invoice No': invoice.invoiceNo,
          'Date': formatDate(invoice.date),
          'Customer': isPhysical ? '' : invoice.customerName,
          'Revenue (₹)': invoice.revenue,
          'Cost (₹)': invoice.cost,
          'Profit/Loss (₹)': invoice.profit === null ? 'N/A' : invoice.profit,
          'Margin (%)': isPhysical ? 'N/A' : (invoice.margin === null ? 'N/A' : invoice.margin.toFixed(2)),
          'Type': isPhysical ? 'Physical Adjustment' : 'Invoice'
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Invoice & Physical Adjustments');
      XLSX.writeFile(workbook, 'invoice_profit_loss_with_physical.xlsx');
      enqueueSnackbar('Report exported successfully', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar('Failed to export report', { variant: 'error' });
    } finally {
      setIsExporting(false);
    }
  };

  const toNumber = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
  };

  /** Whole-rupee display for invoice line dialog (matches summary/list feel; avoids split/qty decimals) */
  const formatCurrencyWhole = (amount) => formatCurrency(Math.round(toNumber(amount)), { decimalPlaces: 0 });

  /** Line amount actually charged (matches invoice P&L revenue), incl. GST when applicable */
  const getLineSellingAmount = (item) => {
    if (item?.total != null && item.total !== '') {
      const t = toNumber(item.total);
      if (Number.isFinite(t) && t >= 0) return t;
    }
    const qty = toNumber(item?.quantity);
    const unit = toNumber(item?.unitPrice);
    const discount = toNumber(item?.discount);
    const tax = toNumber(item?.taxAmount);
    return unit * qty - discount + tax;
  };

  const getItemProductProfit = (item) => {
    const qty = toNumber(item?.quantity);
    const avgCost = toNumber(
      item?.purchaseRate ?? item?.product?.averageCost ?? item?.product?.purchasePrice ?? item?.product?.costPrice
    );
    return getLineSellingAmount(item) - avgCost * qty;
  };

  const handleOpenInvoiceDetails = async (invoice) => {
    if (!invoice?.orderId) return;
    try {
      setLoadingDetails(true);
      setSelectedInvoice(invoice);
      setDetailsOpen(true);
      const order = await salesService.getSalesOrderById(invoice.orderId);
      const items = Array.isArray(order?.items) ? order.items : [];
      setSelectedOrderItems(items);
    } catch (err) {
      enqueueSnackbar('Failed to load invoice product details', { variant: 'error' });
      setSelectedOrderItems([]);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleCloseInvoiceDetails = () => {
    setDetailsOpen(false);
    setSelectedInvoice(null);
    setSelectedOrderItems([]);
  };

  // Load data on component mount
  useEffect(() => {
    handleSearch();
  }, []);

  return (
    <Layout>
      <Box component="main" sx={{ flexGrow: 1, py: 8 }}>
        <Container maxWidth="xl">
          {/* Breadcrumbs */}
          <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 3 }}>
            <Link component={RouterLink} to="/" color="inherit">Dashboard</Link>
            <Link component={RouterLink} to="/reports" color="inherit">Reports</Link>
            <Typography color="text.primary">Invoice-based P&L Report</Typography>
          </Breadcrumbs>

          <Typography variant="h4" gutterBottom>
            Invoice-based Profit & Loss Report
          </Typography>

          {/* Export Button Row */}
          <Grid container justifyContent="flex-end" sx={{ mb: 2 }}>
            <Grid item>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleExportToExcel}
                disabled={isExporting || data.invoiceDetails.length === 0}
                startIcon={isExporting ? <CircularProgress size={20} /> : <FileDownloadIcon />}
                sx={{ minWidth: 180, height: '48px', fontWeight: 600 }}
              >
                {isExporting ? 'Exporting...' : 'Export to Excel'}
              </Button>
            </Grid>
          </Grid>

          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{
                bgcolor: '#1a237e', // Deep blue
                color: 'white',
                boxShadow: 3,
                '&:hover': { boxShadow: 6 }
              }}>
                <CardContent>
                  <Typography color="inherit" gutterBottom variant="subtitle2">
                    Total Invoices
                  </Typography>
                  <Typography color="inherit" variant="h4">
                    {loading ? <CircularProgress size={20} /> : data.totalInvoices}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{
                bgcolor: '#e65100', // Deep orange
                color: 'white',
                boxShadow: 3,
                '&:hover': { boxShadow: 6 }
              }}>
                <CardContent>
                  <Typography color="inherit" gutterBottom variant="subtitle2">
                    Total Invoice Amount
                  </Typography>
                  <Typography color="inherit" variant="h4">
                    {loading ? <CircularProgress size={20} /> : formatCurrency(data.totalRevenue)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{
                bgcolor: '#1b5e20', // Deep green
                color: 'white',
                boxShadow: 3,
                '&:hover': { boxShadow: 6 }
              }}>
                <CardContent>
                  <Typography color="inherit" gutterBottom variant="subtitle2">
                    Total Profit
                  </Typography>
                  <Typography color="inherit" variant="h4">
                    {loading ? <CircularProgress size={20} /> : formatCurrency(data.totalProfit)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{
                bgcolor: '#b71c1c', // Deep red
                color: 'white',
                boxShadow: 3,
                '&:hover': { boxShadow: 6 }
              }}>
                <CardContent>
                  <Typography color="inherit" gutterBottom variant="subtitle2">
                    Total Loss
                  </Typography>
                  <Typography color="inherit" variant="h4">
                    {loading ? <CircularProgress size={20} /> : formatCurrency(data.totalLoss)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{
                bgcolor: '#006064',
                color: 'white',
                boxShadow: 3,
                '&:hover': { boxShadow: 6 }
              }}>
                <CardContent>
                  <Typography color="inherit" gutterBottom variant="subtitle2">
                    Physical Stock Adjustment
                  </Typography>
                  <Typography color="inherit" variant="h4">
                    {loading ? <CircularProgress size={20} /> : formatCurrency(data.physicalAdjustmentAmount)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Search Form */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Filter Options
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Showing data from {startDate} to {endDate} (Today's data by default)
            </Typography>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                 
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Search by Invoice/Customer"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Enter invoice number or customer name"
                  
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleSearch}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                  sx={{ height: '56px' }}
                >
                  Search
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Error Display */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Results Table */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Invoice No</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell align="right">Revenue</TableCell>
                  <TableCell align="right">Cost</TableCell>
                  <TableCell align="right">Profit/Loss</TableCell>
                  <TableCell align="right">Margin (%)</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : data.invoiceDetails.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      No data available. Please search with different criteria.
                    </TableCell>
                  </TableRow>
                ) : (
                  data.invoiceDetails
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((invoice) => {
                      const isPhysical = invoice.type === 'physical';
                      const isDamage = isPhysical && (invoice.reason === 'Damage/Breakage');
                      return (
                        <TableRow
                          key={invoice.invoiceNo}
                          sx={isDamage ? { '& td': { color: '#b71c1c' } } : undefined}
                        >
                          <TableCell>{invoice.invoiceNo}</TableCell>
                          <TableCell>{formatDate(invoice.date)}</TableCell>
                          <TableCell>{isPhysical ? '' : invoice.customerName}</TableCell>
                          <TableCell>{isPhysical ? (invoice.reason || '') : ''}</TableCell>
                          <TableCell align="right">{formatCurrency(invoice.revenue)}</TableCell>
                          <TableCell align="right">{formatCurrency(invoice.cost)}</TableCell>
                          <TableCell
                            align="right"
                            sx={isDamage ? { color: '#b71c1c', fontWeight: 'bold' } : (isPhysical ? {} : { color: invoice.profit >= 0 ? '#1b5e20' : '#b71c1c', fontWeight: 'bold' })}
                          >
                            {invoice.profit !== null ? formatCurrency(invoice.profit) : 'N/A'}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={isDamage ? { color: '#b71c1c' } : (isPhysical ? {} : { color: invoice.margin >= 0 ? '#1b5e20' : '#b71c1c' })}
                          >
                            {invoice.margin !== null ? `${invoice.margin.toFixed(2)}%` : 'N/A'}
                          </TableCell>
                          <TableCell align="center">
                            {isPhysical ? (
                              <Typography variant="body2" color="text.secondary">-</Typography>
                            ) : (
                              <Tooltip title="View invoice product details">
                                <span>
                                  <IconButton
                                    color="primary"
                                    size="small"
                                    onClick={() => handleOpenInvoiceDetails(invoice)}
                                    disabled={!invoice.orderId}
                                  >
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                )}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={data.invoiceDetails.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </TableContainer>

          <Dialog open={detailsOpen} onClose={handleCloseInvoiceDetails} maxWidth="lg" fullWidth>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h6">
                  Invoice Product Profit Details
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedInvoice?.invoiceNo || ''} {selectedInvoice?.customerName ? `- ${selectedInvoice.customerName}` : ''}
                </Typography>
              </Box>
              <IconButton onClick={handleCloseInvoiceDetails}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              {loadingDetails ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  {(() => {
                    const showFifoSplit = selectedOrderItems.some(
                      (item) => item?.fifoCostSplit && String(item.fifoCostSplit).trim() !== ''
                    );
                    return (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Product</TableCell>
                        <TableCell align="right">Qty</TableCell>
                        <TableCell align="right">Cost Per Unit</TableCell>
                        {showFifoSplit && <TableCell>FIFO Cost Split</TableCell>}
                        <TableCell align="right">Selling (incl. GST)</TableCell>
                        <TableCell align="right">Product Profit</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedOrderItems.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={showFifoSplit ? 6 : 5} align="center">No product data available for this invoice.</TableCell>
                        </TableRow>
                      ) : (
                        <>
                          {selectedOrderItems.map((item, index) => {
                            const qty = toNumber(item.quantity);
                            const avgCost = toNumber(item.purchaseRate ?? item.product?.averageCost ?? item.product?.purchasePrice ?? item.product?.costPrice);
                            const lineAmount = getLineSellingAmount(item);
                            const unitSoldInclTax = qty > 0 ? lineAmount / qty : 0;
                            const productProfit = getItemProductProfit(item);
                            return (
                              <TableRow key={item.id || `${item.productId || 'p'}-${index}`}>
                                <TableCell>{item.productName || item.product?.name || `Product #${item.productId || '-'}`}</TableCell>
                                <TableCell align="right">{qty}</TableCell>
                                <TableCell align="right">{formatCurrencyWhole(avgCost)}</TableCell>
                                {showFifoSplit && <TableCell>{item?.fifoCostSplit || '-'}</TableCell>}
                                <TableCell align="right">{formatCurrency(unitSoldInclTax, { decimalPlaces: 2 })}</TableCell>
                                <TableCell align="right" sx={{ color: productProfit >= 0 ? '#1b5e20' : '#b71c1c', fontWeight: 'bold' }}>
                                  {formatCurrencyWhole(productProfit)}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                          <TableRow>
                            <TableCell colSpan={showFifoSplit ? 5 : 4} sx={{ fontWeight: 'bold' }}>Total Product-wise Profit</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold', color: selectedOrderItems.reduce((sum, item) => sum + getItemProductProfit(item), 0) >= 0 ? '#1b5e20' : '#b71c1c' }}>
                              {formatCurrencyWhole(selectedOrderItems.reduce((sum, item) => sum + getItemProductProfit(item), 0))}
                            </TableCell>
                          </TableRow>
                        </>
                      )}
                    </TableBody>
                  </Table>
                    );
                  })()}
                </TableContainer>
              )}
            </DialogContent>
          </Dialog>
        </Container>
      </Box>
    </Layout>
  );
};

export default InvoiceProfitLossReport; 
